import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FaixaDaEsteira, estadoEmPalavras } from '../components/FaixaDaEsteira';
import { jornaisApi, type EsteiraResponse, type Etapa } from '../services/jornais';
import estilos from './EsteiraDaLauda.module.css';
import { sanitizarHtml } from '@/utils/sanitize';

/**
 * A esteira de UMA lauda, de cima para baixo.
 *
 * O humano parametrizou uma vez e a esteira anda sozinha; esta tela é o que ele
 * mantém: ver o que cada etapa devolveu, corrigir o que quiser e mandar refazer
 * dali para a frente.
 *
 * Duas coisas a tela precisa dizer sem que ninguém pergunte: que corrigir uma
 * etapa deixa as seguintes desatualizadas (senão o editor sai achando que o site
 * já mudou) e que etapa corrigida à mão não é re-executada no refazer (que é o
 * motivo de alguém corrigir).
 */

/** Os 5W1H na ordem em que um editor pergunta. Campo vazio aparece como vazio:
 *  buraco no dossiê é informação, e esconder faz o editor achar que apurou. */
const CINCO_W = [
  ['quem', 'Quem'],
  ['o_que', 'O quê'],
  ['quando', 'Quando'],
  ['onde', 'Onde'],
  ['por_que', 'Por quê'],
  ['como', 'Como'],
] as const;

/** Os campos que carregam o subjetivo. Desde 09/09/2026 emoção não é adjetivo
 *  na prosa: é variável fechada aqui dentro. O texto sai da geladeira; o humor
 *  da matéria original viaja como parâmetro. */
const SUBJETIVO = [
  ['tom_de_voz', 'Tom de voz'],
  ['temperatura', 'Temperatura'],
  ['humor', 'Humor'],
  ['urgencia', 'Urgência'],
] as const;

/** A forma da peça de TV também é campo, não prosa. É isto que autoriza a
 *  matéria a ter um parágrafo só: nota coberta sem sonora não tem mais de onde
 *  tirar texto, e um parágrafo é a resposta certa, não entrega incompleta. */
const ESTRUTURA = [
  ['tipo', 'Tipo'],
  ['tem_vt', 'Tem VT'],
  ['tem_transcricao', 'Tem transcrição'],
  ['tem_cabeca', 'Tem cabeça'],
  ['tem_pe', 'Tem pé'],
] as const;

/** Citação e número podem chegar como objeto: o contrato do prompt pede lista de
 *  texto, mas o modelo às vezes devolve `{texto, quem}` — e `String()` num objeto
 *  imprime `[object Object]` na cara do editor. Aqui o objeto vira as suas
 *  próprias palavras, na ordem em que vieram. */
function emTexto(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    const partes = Object.values(v as Record<string, unknown>)
      .filter((x) => x !== null && x !== undefined && x !== '')
      .map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x)));
    return partes.length > 0 ? partes.join(' — ') : '—';
  }
  return String(v);
}

function valorEmPalavras(v: unknown): string {
  if (v === true) return 'sim';
  if (v === false) return 'não';
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/**
 * O DOSSIÊ, que é a única coisa que o escritor recebe.
 *
 * O roteiro cru não chega mais ao modelo desde 09/09/2026. Isso transformou
 * este JSON na fonte da matéria — e uma fonte que só existe como `JSON.stringify`
 * num `<pre>` não é conferível por quem edita. Aqui ele vira leitura: primeiro o
 * lead, depois o 5W1H, depois os parâmetros que mandam no texto. O JSON continua
 * embaixo, inteiro e do jeito que está, porque é ele que o motor obedece.
 */
/** As seções que o quadro do dono pede e que este dossiê NÃO traz — cada uma
 *  com o motivo. Buraco escondido faz o editor achar que foi apurado; buraco
 *  nomeado diz se é para esperar, cobrar, ou parar de esperar.
 *
 *  Medido em 09/09/2026 contra o backend, não presumido. */
const NAO_EXISTEM: { rotulo: string; motivo: string; porque: 'decisao' | 'falta' }[] = [
  {
    rotulo: 'Detalhamento e impacto',
    porque: 'decisao',
    motivo:
      'não sai para telejornal, de propósito: o estágio que desenvolve impacto foi feito ' +
      'para dossiê de várias fontes. Com uma lauda só, ele não desenvolve — ele inventa ' +
      '(motor_telejornal.py). A matéria fica com o que a lauda disse.',
  },
  {
    rotulo: 'Desdobramentos e repercussões',
    porque: 'decisao',
    motivo: 'mesma decisão do detalhamento: reação e próximo passo que a lauda não trouxe seriam invenção.',
  },
  {
    rotulo: 'Aplicações multiplataforma',
    porque: 'falta',
    motivo:
      'não existe no motor. O que já existe são os EXEMPLARES por plataforma, na tela de ' +
      'Jornais — clique na manchete. Uma seção que sugira texto por rede ainda é trabalho a fazer.',
  },
  {
    rotulo: 'Headlines A/B',
    porque: 'falta',
    motivo:
      'a etapa 6 escreve UMA manchete, entre 55 e 75 caracteres. Variante para teste é ' +
      'trabalho a fazer no motor, não campo que a tela esqueceu de mostrar.',
  },
  {
    rotulo: 'Validade editorial',
    porque: 'falta',
    motivo:
      'não existe campo de perecibilidade em lugar nenhum do backend. Enquanto não existir, ' +
      'quem decide se a matéria ainda vale é o editor, sem apoio da esteira.',
  },
];

function Dossie({ s }: { s: Record<string, any> }) {
  const citacoes: unknown[] = Array.isArray(s.citacoes) ? s.citacoes : [];
  const numeros: unknown[] = Array.isArray(s.numeros) ? s.numeros : [];
  return (
    <div className={estilos.dossie}>
      {s.lead ? (
        <p className={estilos.lead}>{String(s.lead)}</p>
      ) : (
        <p className={estilos.vazio}>o dossiê veio sem lead</p>
      )}

      <dl className={estilos.cincoW}>
        {CINCO_W.map(([chave, rotulo]) => (
          <div key={chave} className={estilos.parW}>
            <dt>{rotulo}</dt>
            <dd className={s[chave] ? undefined : estilos.ausente}>{valorEmPalavras(s[chave])}</dd>
          </div>
        ))}
      </dl>

      <div className={estilos.parametros}>
        <section>
          <h4>O subjetivo, em variável</h4>
          <dl>
            {SUBJETIVO.map(([chave, rotulo]) => (
              <div key={chave} className={estilos.parametro}>
                <dt>{rotulo}</dt>
                <dd>{valorEmPalavras(s[chave])}</dd>
              </div>
            ))}
          </dl>
        </section>
        <section>
          <h4>A estrutura da peça</h4>
          <dl>
            {ESTRUTURA.map(([chave, rotulo]) => (
              <div key={chave} className={estilos.parametro}>
                <dt>{rotulo}</dt>
                <dd>{valorEmPalavras(s[chave])}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section className={estilos.lacunas}>
        <h4>O que este dossiê não traz</h4>
        <dl>
          {NAO_EXISTEM.map((l) => (
            <div key={l.rotulo} className={estilos.lacuna} data-porque={l.porque}>
              <dt>
                {l.rotulo}
                <span className={estilos.selo}>
                  {l.porque === 'decisao' ? 'por decisão' : 'ainda não existe'}
                </span>
              </dt>
              <dd>{l.motivo}</dd>
            </div>
          ))}
        </dl>
      </section>

      {citacoes.length > 0 && (
        <section>
          <h4>Citações — só fala do VT autoriza aspas</h4>
          <ul className={estilos.lista}>
            {citacoes.map((c, i) => (
              <li key={i}>{emTexto(c)}</li>
            ))}
          </ul>
        </section>
      )}

      {numeros.length > 0 && (
        <section>
          <h4>Números apurados</h4>
          <ul className={estilos.lista}>
            {numeros.map((n, i) => (
              <li key={i}>{emTexto(n)}</li>
            ))}
          </ul>
        </section>
      )}

      {s.resumo_longo ? (
        <section>
          <h4>A lauda normalizada, inteira</h4>
          <p className={estilos.fala}>{String(s.resumo_longo)}</p>
        </section>
      ) : null}

      <details className={estilos.cru}>
        <summary>O JSON como está — é ele que o motor obedece</summary>
        <pre className={estilos.json}>{JSON.stringify(s, null, 2)}</pre>
      </details>
    </div>
  );
}

/** Como mostrar a saída de cada etapa. O que o editor precisa ler primeiro. */
function Saida({ etapa }: { etapa: Etapa }) {
  const s = etapa.saida ?? {};
  if (etapa.estado === 'pulada') {
    return <p className={estilos.pulada}>{String(s.motivo ?? 'não se aplica a esta lauda')}</p>;
  }
  if (etapa.estado === 'falhou') {
    return <p className={estilos.erro}>{etapa.erro ?? 'falhou sem motivo registrado'}</p>;
  }
  if (!etapa.saida) {
    return <p className={estilos.vazio}>ainda não rodou</p>;
  }

  if (etapa.etapa === 'materia') {
    const html = String(s.content_html ?? '');
    // O corpo guardado aqui é ANTERIOR à resolução dos tokens de mídia: quem
    // troca `[[wd:media …]]` pelo <figure> do vídeo é o publisher, dentro do
    // publish. Então este HTML não é o que está no ar quando a lauda tem VT — e
    // a tela afirmava que era. Em vez de esconder o token, dizemos o que ele é:
    // é ali que o vídeo entra na matéria publicada.
    const tokens = (html.match(/\[\[wd:media\b/g) ?? []).length;
    return (
      <div>
        {tokens > 0 && (
          <p className={estilos.nota}>
            {tokens === 1 ? 'Um marcador de vídeo' : `${tokens} marcadores de vídeo`} no corpo. O
            player só existe na matéria publicada — aqui o texto guardado é o de antes da troca.
          </p>
        )}
        {/* O corpo vem do modelo, que leu a lauda escrita por terceiro no NRCS.
            `sanitizarHtml` e o ponto unico de limpeza do front (utils/sanitize.ts)
            e ja era usado em Editorias e no preview do ArticleCreator; esta tela
            era a unica que injetava cru. */}
        <div
          className={estilos.materia}
          dangerouslySetInnerHTML={{ __html: sanitizarHtml(html) }}
        />
      </div>
    );
  }
  if (etapa.etapa === 'publicacao_site' && s.wp_link) {
    return (
      <p>
        <a href={String(s.wp_link)} target="_blank" rel="noreferrer">
          {String(s.title ?? s.wp_link)}
        </a>
      </p>
    );
  }
  // O VT que chegou do jornal e as versões que o render produziu são a mesma
  // pergunta para quem abre a etapa: cadê o vídeo? Antes a etapa da mídia
  // devolvia `{completude, versão}` — provava que rodou sem entregar nada, e
  // quem quisesse conferir o material tinha de sair da tela para procurá-lo.
  if ((etapa.etapa === 'midia' || etapa.etapa.startsWith('video_')) && s.url) {
    const segundos = Math.round(Number(s.duracao_ms ?? 0) / 1000);
    return (
      <figure className={estilos.video}>
        <video src={String(s.url)} controls preload="metadata" poster={String(s.thumb ?? '')} />
        <figcaption>
          {segundos > 0 ? `${segundos}s` : 'duração não medida'}
          {s.formato ? ` · ${String(s.formato)}` : ''}{' '}
          <a href={String(s.url)} target="_blank" rel="noreferrer">
            abrir o arquivo
          </a>
        </figcaption>
      </figure>
    );
  }

  // O ROTEIRO como ele chegou do ARION, com a marcação de telejornal. O
  // escritor não o recebe mais — lê só o dossiê — mas quem confere precisa da
  // fonte na mão: é ela que responde se a matéria diz o que a lauda dizia.
  if (etapa.etapa === 'recepcao' && s.roteiro) {
    return (
      <div>
        <p className={estilos.fala}>{String(s.roteiro)}</p>
        <p className={estilos.nota}>
          story {String(s.story_id ?? '')} · {String(s.chars ?? '')} caracteres ·{' '}
          {String(s.itens ?? 0)} item(ns) · objeto {String(s.completude ?? '')}
        </p>
      </div>
    );
  }

  // A FALA, e não a contagem dela. É a matéria-prima do que o motor escreveu:
  // sem o texto aqui, ninguém consegue conferir se a matéria diz o que o
  // repórter disse — a única pergunta que importa quando TV vira site.
  if (etapa.etapa === 'transcricao' && s.texto) {
    return (
      <div>
        <p className={estilos.fala}>{String(s.texto)}</p>
        <p className={estilos.nota}>
          {String(s.chars ?? '')} caracteres em {String(s.vts ?? 1)} VT(s)
        </p>
      </div>
    );
  }
  if (etapa.etapa === 'publicacao_youtube') {
    const entradas = Object.entries(s as Record<string, any>);
    return (
      <ul className={estilos.lista}>
        {entradas.map(([formato, r]) => (
          <li key={formato}>
            {formato}:{' '}
            {r?.ok ? (
              <a href={String(r.url)} target="_blank" rel="noreferrer">
                {String(r.video_id)}
              </a>
            ) : (
              <span className={estilos.erro}>{String(r?.error ?? 'não subiu')}</span>
            )}
          </li>
        ))}
      </ul>
    );
  }
  // O DOSSIÊ. Ele caía no `<pre>` de baixo junto com o resto — e é o campo mais
  // importante da esteira desde que o escritor passou a ler só ele. JSON cru num
  // acordeão não é conferível: ninguém lê 5W1H em `"por_que": "..."`.
  // Antes o portão exigia lead OU resumo_longo OU quem. Mas o próprio prompt do
  // motor autoriza campo vazio — e dossiê que voltou vazio é exatamente o caso
  // em que o editor MAIS precisa ver a forma, com os buracos marcados, em vez
  // de um JSON cru que ele não vai ler. Basta ser a etapa certa.
  if (etapa.etapa === 'normalizacao') {
    return <Dossie s={s as Record<string, any>} />;
  }

  return <pre className={estilos.json}>{JSON.stringify(s, null, 2)}</pre>;
}

export function EsteiraDaLauda() {
  const { laudaId } = useParams<{ laudaId: string }>();
  const navegar = useNavigate();
  const id = Number(laudaId);

  const [dados, setDados] = useState<EsteiraResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await jornaisApi.esteira(id));
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não deu para ler a esteira');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function refazer(etapa: string) {
    setOcupado(etapa);
    setAviso(null);
    try {
      const r = await jornaisApi.refazer(id, etapa);
      setAviso(
        r.recusa
          ? `A esteira recusou: ${r.recusa}`
          : r.erro
            ? `Refazer falhou: ${r.erro}`
            : `Refeito a partir de ${etapa}. ${r.marcadas.length} etapa(s) na fila.`,
      );
      await carregar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'não deu para refazer');
    } finally {
      setOcupado(null);
    }
  }

  async function corrigir(etapa: Etapa, texto: string) {
    setOcupado(etapa.etapa);
    setAviso(null);
    try {
      const saida =
        etapa.etapa === 'materia'
          ? { ...(etapa.saida ?? {}), content_html: texto }
          : { ...(etapa.saida ?? {}), texto };
      const r = await jornaisApi.corrigir(id, etapa.etapa, saida);
      setAviso(
        r.invalidadas.length
          ? `Correção salva. ${r.invalidadas.length} etapa(s) ficaram desatualizadas — refaça a partir daqui para o site refletir.`
          : 'Correção salva.',
      );
      await carregar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'não deu para corrigir');
    } finally {
      setOcupado(null);
    }
  }

  if (carregando) return <div className={estilos.pagina}>Carregando a esteira…</div>;
  if (erro) return <div className={estilos.pagina}>{erro}</div>;
  if (!dados) return null;

  const porEtapa = new Map(dados.etapas.map((e) => [e.etapa, e]));

  return (
    <div className={estilos.pagina}>
      <button type="button" className={estilos.voltar} onClick={() => navegar('/jornais')}>
        ← Jornais
      </button>

      <header className={estilos.cabecalho}>
        <h1>{dados.titulo ?? `Lauda ${dados.story_id}`}</h1>
        <p className={estilos.subtitulo}>
          story {dados.story_id} · objeto {dados.completude}
          {dados.estado_pauta ? ` · ${dados.estado_pauta}` : ''}
          {dados.resumo.travada_em ? (
            <span className={estilos.travada}> · parada em {dados.resumo.travada_em}</span>
          ) : null}
        </p>
        <FaixaDaEsteira
          catalogo={dados.catalogo}
          resumo={dados.resumo}
          onEtapa={(e) => setAberta(e)}
        />
      </header>

      {aviso && (
        <p className={estilos.aviso} role="status">
          {aviso}
        </p>
      )}

      <ol className={estilos.etapas}>
        {dados.catalogo.map((c) => {
          const etapa = porEtapa.get(c.etapa);
          const estado = etapa?.estado ?? 'ausente';
          const abertaAgora = aberta === c.etapa;
          return (
            <li key={c.etapa} className={estilos.etapa} data-estado={estado}>
              <button
                type="button"
                className={estilos.linha}
                onClick={() => setAberta(abertaAgora ? null : c.etapa)}
                aria-expanded={abertaAgora}
                /* O rótulo acessível diz a etapa E o estado: quem navega por
                   leitor de tela não vê a cor da faixa nem a coluna ao lado. */
                aria-label={`Etapa ${c.ordem}, ${c.rotulo}: ${
                  etapa?.manual ? 'corrigida à mão' : estadoEmPalavras(estado)
                }`}
              >
                <span className={estilos.ordem}>{c.ordem}</span>
                <span className={estilos.nome}>{c.rotulo}</span>
                <span className={estilos.estado}>
                  {etapa?.manual ? 'corrigida à mão' : estadoEmPalavras(estado)}
                </span>
                {etapa?.ator && <span className={estilos.ator}>{etapa.ator}</span>}
                {etapa?.versao && etapa.versao > 1 && (
                  <span className={estilos.versao}>v{etapa.versao}</span>
                )}
              </button>

              {abertaAgora && (
                <div className={estilos.corpo}>
                  {etapa ? <Saida etapa={etapa} /> : <p className={estilos.vazio}>ainda não rodou</p>}

                  <div className={estilos.acoes}>
                    <button
                      type="button"
                      disabled={ocupado !== null}
                      onClick={() => void refazer(c.etapa)}
                    >
                      {ocupado === c.etapa ? 'refazendo…' : 'Refazer daqui'}
                    </button>
                    {etapa && (etapa.etapa === 'materia' || etapa.etapa === 'normalizacao') && (
                      <CorrigirEtapa
                        etapa={etapa}
                        ocupado={ocupado !== null}
                        onSalvar={(texto) => void corrigir(etapa, texto)}
                      />
                    )}
                  </div>

                  {etapa?.manual && (
                    <p className={estilos.nota}>
                      Esta etapa foi corrigida à mão. Ao refazer, ela é preservada e a correção
                      entra como fonte da etapa seguinte.
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** O editor corrige a saída; a tela avisa que isso desatualiza o que vem depois. */
function CorrigirEtapa({
  etapa,
  ocupado,
  onSalvar,
}: {
  etapa: Etapa;
  ocupado: boolean;
  onSalvar: (texto: string) => void;
}) {
  const inicial =
    etapa.etapa === 'materia'
      ? String(etapa.saida?.content_html ?? '')
      : String(etapa.saida?.resumo_longo ?? etapa.saida?.texto ?? '');
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(inicial);

  if (!editando) {
    return (
      <button type="button" disabled={ocupado} onClick={() => setEditando(true)}>
        Corrigir
      </button>
    );
  }
  return (
    <div className={estilos.editor}>
      <label htmlFor={`corrigir-${etapa.etapa}`}>Saída desta etapa</label>
      <textarea
        id={`corrigir-${etapa.etapa}`}
        value={texto}
        rows={10}
        onChange={(e) => setTexto(e.target.value)}
      />
      <p className={estilos.nota}>
        Salvar deixa as etapas seguintes desatualizadas. Depois disso, use “Refazer daqui”.
      </p>
      <div className={estilos.acoes}>
        <button type="button" disabled={ocupado} onClick={() => onSalvar(texto)}>
          Salvar correção
        </button>
        <button type="button" onClick={() => setEditando(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
